import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ficheOuverteSurPlace,
  lireLArrivee,
  naviguerDansLaFeuille,
  navigationDeDepart,
  type ArriveeSurLaFeuille,
  type GesteDeNavigation,
} from '../partage/navigationDeLaFeuille';

/**
 * La navigation de la Feuille, tenue (chantier nettoyage-feuille, ticket 02) : la règle est
 * `partage/navigationDeLaFeuille.ts`. La Feuille ne se remonte pas d'une adresse à l'autre :
 * chaque adresse nouvelle (une autre feuille, ou une arrivée sur la même) passe par la règle
 * pendant le rendu, sans `useEffect`, et la feuille ne se dessine jamais avec l'état de la
 * précédente.
 */
export function useNavigationDeLaFeuille(feuille: string) {
  const lieu = useLocation();
  const arrivee: ArriveeSurLaFeuille = lireLArrivee(lieu.state);
  const [tenue, setTenue] = useState(() => navigationDeDepart(feuille, lieu.key, arrivee));
  let nav = tenue;
  if (nav.feuille !== feuille || nav.cle !== lieu.key) {
    nav = naviguerDansLaFeuille(nav, { sorte: 'arriver', feuille, cle: lieu.key, arrivee });
    setTenue(nav);
  }
  // Les gestes ne changent pas d'un rendu à l'autre, comme le `setFicheOuverte` d'avant.
  const gestes = useMemo(() => {
    const faire = (geste: GesteDeNavigation) => setTenue((avant) => naviguerDansLaFeuille(avant, geste));
    return {
      ouvrir: (id: string) => faire({ sorte: 'ouvrir', id }),
      ouvrirSurPlace: (id: string) => faire({ sorte: 'ouvrir-sur-place', id }),
      fermer: () => faire({ sorte: 'fermer' }),
      garder: (id: string) => faire({ sorte: 'garder', id }),
    };
  }, []);
  return {
    arrivee,
    /** La clé de l'arrivée : une par navigation, même sur la même feuille. */
    cle: lieu.key,
    fiche: nav.fiche,
    surPlace: ficheOuverteSurPlace(nav),
    gardees: nav.gardees,
    ...gestes,
  };
}
